import { SessionType } from '../../webrtc/session/constants';
import { type ProviderRealtimeService } from './realtime';
import { SessionEventType } from '../../webrtc/session';
import { type ServiceTokens } from '../../container';
import { CallState, CallTransition } from '../../features/call/call.types';
import {
  GenericCallService,
  type GenericCallServiceDependencies,
} from './genericCall.service';
import { addPendingParticipants } from '../../features/session/session.slice';
import { store } from '../../stores/redux';
import { setDbSessionId } from '../../features/apolloSession/apolloSession.slice';
import { ManualVideoQuality } from './interface';
import { RealtimeMessageType } from '../../features/realtime/realtime.types';
import { getDefaultVideoQuality } from '../../shared/deviceCapabilities/getDefaultVideoQuality';
import { type ProviderAuthCredentials } from '../../services/provider/authService';
import { CallHistoryService } from './history/callHistory.service';
import type { PhoneMember, StartCallParticipant } from './types';
import { CallType } from '../../shared/types';

const hasPhoneMember = (
  participant: StartCallParticipant,
): participant is PhoneMember => participant.isPhoneParticipant === true;

type StartCallOptions = {
  identityId: string;
  isAudioCall?: boolean;
  callType?: CallType;
  sessionType: SessionType;
  participants?: StartCallParticipant[];
  startCallTime?: number;
  credentials: ProviderAuthCredentials;
};
type AddParticipantsOptions = {
  isAudioCall: boolean;
  participants: StartCallParticipant[];
  startCallTime: number;
  credentials: ProviderAuthCredentials;
};
type ResumeCallOptions = {
  isAudioCall: boolean;
  dbSessionId: string;
  identityId: string;
  participants?: string[];
  startCallTime?: number;
  ownershipToken: string;
  credentials: ProviderAuthCredentials;
};
type ProviderCallServiceDependencies = {
  [ServiceTokens.ProviderRealtime]: ProviderRealtimeService;
  [ServiceTokens.CallHistory]: CallHistoryService;
} & GenericCallServiceDependencies;

export class ProviderCallService extends GenericCallService {
  protected override readonly realtime: ProviderRealtimeService;
  protected readonly callHistory: CallHistoryService;

  constructor(dependencies: ProviderCallServiceDependencies) {
    super(dependencies);

    this.realtime = dependencies.providerRealtime;
    this.callHistory = dependencies.callHistory;
    this.syncManualVideoQualityWithAllParticipants();
  }

  async start(
    {
      isAudioCall = false,
      callType = isAudioCall ? CallType.Audio : CallType.Video,
      sessionType = SessionType.Routed,
      participants = [],
      identityId,
      startCallTime = Date.now(),
      credentials,
    }: StartCallOptions = {} as unknown as StartCallOptions,
  ): Promise<{ sessionId?: string; dbSessionId?: string } | undefined> {
    if (
      this.stateMachine.getSnapshot().matches(CallState.Initializing) ||
      this.stateMachine.getSnapshot().matches(CallState.Resuming) ||
      this.stateMachine.getSnapshot().matches(CallState.PhoneCallInitializing)
    ) {
      return;
    }
    const hasPhoneParticipants = participants.some(hasPhoneMember);
    this.stateMachine.send(
      hasPhoneParticipants
        ? CallTransition.START_PHONE_CALL
        : CallTransition.START,
    );

    try {
      const realtimeParticipants = participants.filter(
        (p) => !p.isPhoneParticipant,
      );
      const realtimeParticipantIds = realtimeParticipants.map((p) => p.id);
      const phoneParticipants = participants.filter(hasPhoneMember);
      const phoneParticipantIds = phoneParticipants.map((p) => p.id);
      await this.session.start({
        sessionType,
        identityId: identityId,
        credentials,
        /**
         * We need to pass the participant identity ids to the session start
         * so that the session join tokens will be generated.
         */
        identityIds: [identityId, ...realtimeParticipantIds],
      });

      const joinTokens =
        this.session.joinTokens?.filter(({ identityId }) =>
          realtimeParticipantIds.includes(identityId),
        ) ?? [];

      const dbSessionId = await this.callHistory.recordCallStart({
        sessionId: this.session.sessionId as string,
        userId: Number(credentials.userId),
        callType,
        patients: participants,
      });

      this.realtime.inviteMembers(joinTokens, {
        dbSessionId,
        isAudioCall,
        sessionId: this.session.sessionId as string,
        startCallTime,
      });

      if (phoneParticipants.length > 0) {
        await this.session.addPhoneParticipants({
          phoneParticipants: phoneParticipants.map((p) => ({
            identityId: p.id,
            phoneNumber: p.phoneNumber,
            username: p.username,
          })),
        });
      }

      store.dispatch(
        addPendingParticipants([
          ...realtimeParticipantIds,
          ...phoneParticipantIds,
        ]),
      );

      await this.session.connect();
      if (isAudioCall) {
        // this is an error we should catch here, it shouldn't fail the call.
        void this.media
          .toggleVideo(false)
          .catch((error) => this.logger.warn(error));
      }
      void this.stateMachine.send(CallTransition.INIT_DONE);

      this.realtime.setStatusToBusy();

      return { sessionId: this.session.sessionId, dbSessionId };
    } catch (error) {
      this.logger.error('start call failed', error);
      void this.stateMachine.send(CallTransition.INIT_FAILED);
      return undefined;
    }
  }

  /**
   * Add participants to the existing session.
   */
  async addParticipants({
    startCallTime,
    isAudioCall,
    participants,
    credentials,
  }: AddParticipantsOptions): Promise<void> {
    const realtimeParticipants = participants.filter(
      (p) => !p.isPhoneParticipant,
    );
    const phoneParticipants = participants.filter(hasPhoneMember);
    const realtimeParticipantIds = realtimeParticipants.map((p) => p.id);
    const participantIdentityIds = participants.map((p) => p.id);

    try {
      if (realtimeParticipantIds.length > 0) {
        await this.session.addParticipants({
          identityIds: realtimeParticipantIds,
          credentials,
        });
      }
      if (phoneParticipants.length > 0) {
        await this.session.addPhoneParticipants({
          phoneParticipants: phoneParticipants.map((p) => ({
            identityId: p.id,
            phoneNumber: p.phoneNumber,
            username: p.username,
          })),
        });
      }

      void this.callHistory.recordCallParticipants({
        userId: Number(credentials.userId),
        patients: participants,
      });
    } catch (error) {
      this.logger.error('add participants failed', error);
      return;
    }
    const joinTokens =
      this.session.joinTokens?.filter(({ identityId }) =>
        participantIdentityIds.includes(identityId),
      ) ?? [];

    this.realtime.inviteMembers(joinTokens, {
      dbSessionId: this.callHistory.currentDbSessionId as string,
      joinedParticipantsIds: this.session.allIdentityIds,
      startCallTime,
      isAudioCall,
      sessionId: this.session.sessionId as string,
    });
    store.dispatch(addPendingParticipants(participantIdentityIds));
  }

  async stop(): Promise<void> {
    this.logger.debug('stopped');
  }

  async hold(): Promise<void> {
    this.stateMachine.send(CallTransition.REDIRECTING_TO_DASHBOARD);
    try {
      await this.realtime.putMembersOnHold(
        this.getParticipantIds(),
        this.session.sessionOwnershipToken as string,
      );
    } catch {
      this.logger.warn('failed to notify users of hold call');
    }
    this.session.clearParticipants();
    await this.session.hold();
    this.realtime.setStatusToAvailable();
  }

  async resume(
    {
      isAudioCall,
      identityId,
      dbSessionId,
      participants = [],
      ownershipToken,
      credentials,
      startCallTime = Date.now(),
    }: ResumeCallOptions = {} as unknown as ResumeCallOptions,
  ): Promise<void> {
    if (
      this.stateMachine.getSnapshot().matches(CallState.Initializing) ||
      this.stateMachine.getSnapshot().matches(CallState.Resuming) ||
      this.stateMachine.getSnapshot().matches(CallState.PhoneCallInitializing)
    ) {
      return;
    }

    try {
      this.stateMachine.send(CallTransition.RESUME);
      if (isAudioCall) {
        void this.media
          .toggleVideo(false)
          .catch((error) => this.logger.warn(error));
      }

      await this.session.resume({
        ownershipToken,
        identityId,
        identityIds: [identityId, ...participants],
        credentials,
      });

      const joinTokens =
        this.session.joinTokens?.filter(({ identityId }) =>
          participants.includes(identityId),
        ) ?? [];

      await this.realtime
        .resumeMembersOnHold(joinTokens, {
          dbSessionId,
          isAudioCall,
          sessionId: this.session.sessionId as string,
          startCallTime,
        })
        .catch((error) => this.logger.warn(error));

      store.dispatch(addPendingParticipants(participants));
      store.dispatch(setDbSessionId(dbSessionId));
      await this.session.connect();
      void this.stateMachine.send(CallTransition.INIT_DONE);
      this.realtime.setStatusToBusy();
    } catch (error) {
      this.logger.error('resume call failed', error);
      void this.stateMachine.send(CallTransition.INIT_FAILED);
    }
  }

  async setManualVideoQuality(
    manualVideoQuality: ManualVideoQuality,
  ): Promise<void> {
    if (this._manualVideoQuality === manualVideoQuality) {
      return;
    }

    this._manualVideoQuality = manualVideoQuality;

    const videoQualityProfile = manualVideoQuality || getDefaultVideoQuality();

    /**
     * Provider sets own video quality.
     */
    this.setVideoQuality(videoQualityProfile, 'provider set quality');

    /**
     * Send new manual video quality to all participants.
     */
    Object.values(this.session.participantsMap).forEach((participant) => {
      this.realtime.send(participant.id, {
        messageType: RealtimeMessageType.ManualVideoQuality,
        payload: manualVideoQuality,
      });
    });
  }

  private syncManualVideoQualityWithAllParticipants(): void {
    this.session.on([SessionEventType.ParticipantJoined], ({ data }) => {
      /**
       * For new patients if no manual video quality is set, do nothing
       */
      if (!this.manualVideoQuality) {
        return;
      }
      if (data?.participantId !== undefined) {
        this.realtime.send(data.participantId, {
          messageType: RealtimeMessageType.ManualVideoQuality,
          payload: this.manualVideoQuality,
        });
      }
    });
  }
}
